import { useEffect, useState } from 'react';
import axios from 'axios';
import Cookies from 'universal-cookie';
import {
    Card,
    Container,
    Stack,
    Typography,
    Box,
    TextField,
    MenuItem,
    Table,
    TableHead,
    TableRow,
    TableBody,
    TableCell,
    TablePagination,
    Tabs,
    Tab,
    Button,
    CircularProgress,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { API_URL } from '../../api';
import AccountStatusChips from '../../components/AccountStatusChips';
import HowItWorksCard from '../../components/HowItWorksCard';
import { AccountLifecycleHelp } from '../../components/AccountLifecycleHelp';

const cookies = new Cookies();
const LEVELS = ['', 'KS2', 'KS3', 'GCSE', 'A-Level'];

const ACCOUNT_STATUS_OPTIONS = [
    { value: '', label: 'Any account status' },
    { value: 'pending_approval', label: 'Pending approval' },
    { value: 'approved', label: 'Approved' },
    { value: 'declined', label: 'Declined' },
];
const ENGAGEMENT_STATUS_OPTIONS = [
    { value: '', label: 'Any engagement' },
    { value: 'enrolled', label: 'Enrolled' },
    { value: 'not_enrolled', label: 'Not enrolled' },
    { value: 'on_break', label: 'On break' },
];

export default function StudentsPage() {
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState('');
    const [level, setLevel] = useState('');
    const [subjects, setSubjects] = useState([]);
    const [subject, setSubject] = useState('');
    const [accountStatus, setAccountStatus] = useState('');
    const [engagementStatus, setEngagementStatus] = useState('');
    const [tab, setTab] = useState('active');

    useEffect(() => {
        axios.get(`${API_URL}/api/subjects`).then((res) => setSubjects(res.data || [])).catch(() => {});
    }, []);

    const load = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            params.set('role', 'student');
            params.set('page', page + 1);
            params.set('limit', rowsPerPage);
            if (q.trim()) params.set('q', q.trim());
            if (level) params.set('level', level);
            if (subject) params.set('subject', subject);
            if (tab === 'archived') {
                params.set('accountStatus', 'archived');
            } else {
                if (accountStatus) params.set('accountStatus', accountStatus);
                else params.set('excludeArchived', '1');
            }
            if (engagementStatus) params.set('engagementStatus', engagementStatus);
            const { data } = await axios.get(`${API_URL}/api/admin/accounts?${params.toString()}`, {
                headers: { Authorization: cookies.get('TOKEN') },
            });
            setItems(data.items || []);
            setTotal(data.total || 0);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, [page, rowsPerPage, tab]);

    const submit = (e) => {
        e.preventDefault();
        if (page !== 0) setPage(0);
        else load();
    };

    return (
        <Container>
            <Typography variant="h4">Students</Typography>
            <HowItWorksCard>
                <AccountLifecycleHelp roleLabel="student" />
            </HowItWorksCard>
            <Box sx={{ mb: 3 }} />

            <Tabs
                value={tab}
                onChange={(_, v) => {
                    setTab(v);
                    if (page !== 0) setPage(0);
                }}
                sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
            >
                <Tab value="active" label="Active" />
                <Tab value="archived" label="Archived" />
            </Tabs>

            <Card sx={{ p: 2, mb: 3 }}>
                <Box component="form" onSubmit={submit} sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <TextField
                        label="Search name / email"
                        size="small"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        sx={{ minWidth: 260 }}
                    />
                    <TextField
                        select
                        size="small"
                        label="Level"
                        value={level}
                        onChange={(e) => setLevel(e.target.value)}
                        sx={{ minWidth: 140 }}
                    >
                        {LEVELS.map((l) => (
                            <MenuItem key={l || 'any'} value={l}>
                                {l || 'Any'}
                            </MenuItem>
                        ))}
                    </TextField>
                    <TextField
                        select
                        size="small"
                        label="Subject"
                        value={subject}
                        onChange={(e) => setSubject(e.target.value)}
                        sx={{ minWidth: 200 }}
                    >
                        <MenuItem value="">Any</MenuItem>
                        {subjects.map((s) => (
                            <MenuItem key={s._id} value={s.name}>
                                {s.name}
                            </MenuItem>
                        ))}
                    </TextField>
                    {tab === 'active' && (
                        <TextField
                            select
                            size="small"
                            label="Account status"
                            value={accountStatus}
                            onChange={(e) => setAccountStatus(e.target.value)}
                            sx={{ minWidth: 180 }}
                        >
                            {ACCOUNT_STATUS_OPTIONS.map((o) => (
                                <MenuItem key={o.value || 'any'} value={o.value}>
                                    {o.label}
                                </MenuItem>
                            ))}
                        </TextField>
                    )}
                    <TextField
                        select
                        size="small"
                        label="Engagement"
                        value={engagementStatus}
                        onChange={(e) => setEngagementStatus(e.target.value)}
                        sx={{ minWidth: 170 }}
                    >
                        {ENGAGEMENT_STATUS_OPTIONS.map((o) => (
                            <MenuItem key={o.value || 'any'} value={o.value}>
                                {o.label}
                            </MenuItem>
                        ))}
                    </TextField>
                    <Button type="submit" variant="contained">
                        Filter
                    </Button>
                </Box>
            </Card>

            <Card>
                {loading ? (
                    <Stack alignItems="center" sx={{ py: 6 }}>
                        <CircularProgress />
                    </Stack>
                ) : (
                    <>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Student</TableCell>
                                    <TableCell>Email</TableCell>
                                    <TableCell>Parent</TableCell>
                                    <TableCell>Year group</TableCell>
                                    <TableCell>Subject</TableCell>
                                    <TableCell>Tutor</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell align="right" />
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {items.map((s) => (
                                    <TableRow key={s._id}>
                                        <TableCell>
                                            {s.firstName} {s.surname}
                                        </TableCell>
                                        <TableCell>{s.email}</TableCell>
                                        <TableCell>
                                            {s.parentId && typeof s.parentId === 'object' ? (
                                                <>
                                                    {s.parentId.firstName} {s.parentId.surname}
                                                    <Typography variant="caption" component="div" color="text.secondary">
                                                        {s.parentId.email}
                                                    </Typography>
                                                </>
                                            ) : (
                                                '—'
                                            )}
                                        </TableCell>
                                        <TableCell>{s.yearGroup || '—'}</TableCell>
                                        <TableCell>{s.subject || '—'}</TableCell>
                                        <TableCell>
                                            {s.assignedTutorId
                                                ? `${s.assignedTutorId.firstName} ${s.assignedTutorId.surname}`
                                                : '—'}
                                        </TableCell>
                                        <TableCell>
                                            <AccountStatusChips account={s} />
                                        </TableCell>
                                        <TableCell align="right">
                                            <Button component={RouterLink} to={`/students/${s._id}`} size="small">
                                                Open
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                                {items.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={8}>
                                            <Typography align="center" color="text.secondary" sx={{ py: 3 }}>
                                                No students found.
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                        <TablePagination
                            component="div"
                            count={total}
                            page={page}
                            onPageChange={(_, p) => setPage(p)}
                            rowsPerPage={rowsPerPage}
                            onRowsPerPageChange={(e) => {
                                setRowsPerPage(parseInt(e.target.value, 10));
                                setPage(0);
                            }}
                        />
                    </>
                )}
            </Card>
        </Container>
    );
}
